/** Deutsche Formatierung für Texte in App und Mitteilungen. */
import { dayKeyDiff, localDateKey, zonedParts } from './time';

const WEEKDAY_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const WEEKDAY_LONG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const MONTHS = ['Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

/** „Di, 30.9.“ */
export function fmtDateShort(date: Date, tz: string): string {
  const p = zonedParts(date, tz);
  return `${WEEKDAY_SHORT[p.weekday]}, ${p.day}.${p.month}.`;
}

/** „Dienstag, 30. September“ */
export function fmtDateLong(date: Date, tz: string): string {
  const p = zonedParts(date, tz);
  return `${WEEKDAY_LONG[p.weekday]}, ${p.day}. ${MONTHS[p.month - 1]}`;
}

/** „9:05“ */
export function fmtTime(date: Date, tz: string): string {
  const p = zonedParts(date, tz);
  return `${p.hour}:${String(p.minute).padStart(2, '0')}`;
}

/** Relativ zu heute: „heute“, „morgen“, „in 3 Tagen“, „seit gestern“, „seit 4 Tagen“. */
export function fmtDueRelative(days: number): string {
  if (days === 0) return 'heute';
  if (days === 1) return 'morgen';
  if (days === 2) return 'übermorgen';
  if (days > 2) return `in ${days} Tagen`;
  if (days === -1) return 'seit gestern';
  return `seit ${-days} Tagen`;
}

/** Wie lange ist etwas her: „heute“, „gestern“, „vor 3 Tagen“. */
export function fmtAgo(date: Date, now: Date, tz: string): string {
  const d = dayKeyDiff(localDateKey(date, tz), localDateKey(now, tz));
  if (d <= 0) return 'heute';
  if (d === 1) return 'gestern';
  if (d < 14) return `vor ${d} Tagen`;
  const w = Math.round(d / 7);
  if (d < 60) return `vor ${w} Wochen`;
  return `vor ${Math.round(d / 30)} Monaten`;
}

export function fmtNumber(n: number, digits = 0): string {
  return n.toLocaleString('de-AT', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** „×1,08“ */
export function fmtFactor(f: number): string {
  return `×${fmtNumber(f, 2)}`;
}

/** „etwa alle 6,5 Tage“ */
export function fmtDays(days: number): string {
  if (days < 1.25) return 'täglich';
  const rounded = days < 10 ? Math.round(days * 2) / 2 : Math.round(days);
  const txt = Number.isInteger(rounded) ? fmtNumber(rounded) : fmtNumber(rounded, 1);
  return `alle ${txt} Tage`;
}

/** „A“, „A & B“, „A, B & C“ */
export function fmtList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} & ${items[items.length - 1]}`;
}

/** Stunden als „12,1 h“ */
export function fmtHours(hours: number): string {
  return `${fmtNumber(hours, 1)} h`;
}

export function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}
